/**
 * cognitoClient — contrato de autenticação contra o Amazon Cognito.
 *
 * O fluxo é o `user-password`/SRP nativo do Cognito (contrato Identity Flow em
 * `docs/infra-contract.md`): `POST /auth/login` é o ponto lógico de troca de
 * credencial por token (InitiateAuth SRP), sem Lambda REST própria. O desafio
 * MFA TOTP (sempre exigido para o grupo `admin`, RN-M1-001) é concluído pelo
 * mesmo fluxo (RespondToAuthChallenge).
 *
 * A implementação concreta (`CognitoIdentityClient`) vive em
 * `cognitoIdentityClient.ts`. Aqui fica o contrato consumido pelo AuthContext,
 * para que rotas e componentes sejam tipados.
 *
 * Nenhum dado pessoal é logado (RN-M11-001).
 */

/** Sessão retornada após autenticação bem-sucedida (tokens na aba — ADR-031). */
export interface CognitoSession {
  /** JWT de acesso (chamadas diretas ao Cognito, ex.: cadastro de TOTP). */
  readonly accessToken: string;
  /**
   * JWT de identidade (claims: sub, email, grupos `cognito:groups`). É o token
   * enviado no `Authorization: Bearer` das chamadas à API (ver `authToken.ts`).
   */
  readonly idToken: string;
  /** Token de refresh — mantido pelo SDK na `sessionStorage` da aba (ADR-031; sem cookie). */
  readonly refreshToken: string;
  /** Momento (epoch ms) em que o accessToken expira. */
  readonly expiresAt: number;
}

/** Desafio MFA TOTP pendente (RN-M1-001) — a UI pede o código de 6 dígitos. */
export interface MfaChallenge {
  readonly challenge: 'MFA_TOTP';
}

/**
 * Primeiro acesso (convite ou seed do admin): o Cognito exige a troca da senha
 * temporária (`NEW_PASSWORD_REQUIRED`). A UI pede a nova senha e chama
 * `completeNewPassword`.
 */
export interface NovaSenhaChallenge {
  readonly challenge: 'NOVA_SENHA';
}

/** Resultado de `signIn`: sessão concluída OU um desafio pendente. */
export type SignInResult = CognitoSession | MfaChallenge | NovaSenhaChallenge;

/** Discrimina o resultado de `signIn` como desafio MFA. */
export function isMfaChallenge(result: SignInResult): result is MfaChallenge {
  return (result as MfaChallenge).challenge === 'MFA_TOTP';
}

/** Discrimina o resultado de `signIn` como troca obrigatória de senha. */
export function isNovaSenhaChallenge(result: SignInResult): result is NovaSenhaChallenge {
  return (result as NovaSenhaChallenge).challenge === 'NOVA_SENHA';
}

/** Contrato do cliente de autenticação. */
export interface CognitoClient {
  /** Autentica com e-mail e senha; pode retornar um desafio MFA TOTP. */
  signIn(email: string, password: string): Promise<SignInResult>;
  /** Conclui o desafio MFA TOTP (código de 6 dígitos). */
  confirmMfa(code: string): Promise<CognitoSession>;
  /** Conclui a troca obrigatória de senha do primeiro acesso. */
  completeNewPassword(newPassword: string): Promise<SignInResult>;
  /**
   * Inicia o cadastro do TOTP da sessão corrente (`AssociateSoftwareToken`).
   * @returns o segredo (base32) a ser mostrado no QR `otpauth://`.
   */
  associarTotp(): Promise<string>;
  /**
   * Verifica o primeiro código do aplicativo (`VerifySoftwareToken`) e ativa o
   * TOTP como fator preferido (`SetUserMFAPreference`).
   */
  verificarTotp(code: string): Promise<void>;
  /** Inicia a recuperação de senha (envia código por e-mail via SES). */
  forgotPassword(email: string): Promise<void>;
  /** Conclui a recuperação de senha com o código recebido e a nova senha. */
  confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void>;
  /** Encerra a sessão local (limpa tokens em memória). */
  signOut(): Promise<void>;
  /**
   * Sessão corrente (restaurada da aba após F5, renovada se o id token venceu); com
   * `forcar`, renova pelo refresh token mesmo com o token válido (ADR-031). `null` =
   * sem sessão ou refresh inválido.
   */
  refresh(forcar?: boolean): Promise<CognitoSession | null>;
}
